import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import Chart from "react-apexcharts";
import { Link, useNavigate } from "react-router-dom";
import HealthDetailDrawer from "../Components/HealthDetailDrawer";
import { ChevronLeft, ChevronRight, Info } from "lucide-react";
import { images } from "../Assets/images";
import { GlobalContext } from "../Context/GlobalContext";
import BiometricDrawer from "../Components/BiometricDrawer";
import { normalizeScanDetails } from "./scanDetails/scanNormalizer";
import Loader from "../Components/Loader";
import {
  getCategoryStyle,
  statusIconMap,
  getWellnessMessage,
  getWellnessLabel,
  filterOrder,
} from "../utils";

const Dashboard = () => {
  const {
    getScanDetails,
    scanDetails,
    translatedLabels,
    logoUrl,
    wellnessLabel,
    is4Life,
  } = useContext(GlobalContext);
  const screenLabels = translatedLabels?.common;

  const normalizedScanDetails = useMemo(
    () => normalizeScanDetails(scanDetails, screenLabels),
    [scanDetails, screenLabels],
  );

  const navigate = useNavigate();
  const [selectedMetricData, setSelectedMetricData] = useState(null);
  const scrollRef = useRef(null);
  const [showCardDetail, setShowCardDetail] = useState(false);
  const [activeTab, setActiveTab] = useState("heartRate");
  const [showIconAndImage, setShowIconAndImage] = useState(true);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const openDrawer = (metricData, withIcon = false, tab = null) => {
    if (tab) setActiveTab(tab);
    setSelectedMetricData(metricData);
    setShowIconAndImage(withIcon);
    setShowCardDetail(true);

    window.history.pushState({ modalOpen: true }, "");
  };

  const closeDrawer = () => {
    setShowCardDetail(false);
    if (window.history.state?.modalOpen) {
      window.history.back();
    }
  };

  useEffect(() => {
    const handlePopState = () => {
      if (showCardDetail) {
        setShowCardDetail(false);
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [showCardDetail]);

  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  const healthCards = useMemo(() => {
    if (!normalizedScanDetails || normalizedScanDetails.length === 0) return [];

    const importantMetrics = [
      "blood_pressure",
      "heart_rate",
      "oxygen_saturation",
      "stress_level",
      "hemoglobin",
    ];

    const cards = [];

    Object.values(normalizedScanDetails)?.forEach((category) => {
      category?.metrics?.forEach((metric) => {
        if (!importantMetrics.includes(metric.key)) return;
        if (!metric?.value) return;
        cards.push({
          id: `${category.category}-${metric.key}`,
          key: category.category.toLowerCase().replace(/\s/g, "_"),
          title: metric.label || metric.key,
          category: metric.label || metric.comment || metric.key,
          categoryScore: `${metric.value ?? "-"} ${metric.unit || ""}`.trim(),
          avgReadingScore:
            metric.avg != null
              ? `${metric.avg} ${metric.unit || ""}`.trim()
              : "-",
          rawMetric: {
            key: metric.key,
            value: metric.value,
            unit: metric.unit,
            avg: metric.avg ?? null,
            comment: metric.comment || metric.label || metric.key,
            status: metric.status,
            normal: metric.normal,
          },
        });
      });
    });

    const customOrder = [
      "blood_pressure",
      "heart_rate",
      "oxygen_saturation",
      "stress_level",
      "hemoglobin",
    ];

    cards.sort((a, b) => {
      const indexA = customOrder.indexOf(a.rawMetric.key);
      const indexB = customOrder.indexOf(b.rawMetric.key);
      return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
    });

    return cards;
  }, [normalizedScanDetails]);

  useEffect(() => {
    getScanDetails();
  }, [getScanDetails]);

  const checkOverflow = () => {
    const el = scrollRef.current;
    if (el) {
      setIsOverflowing(el.scrollWidth > el.clientWidth);
    }
  };

  useEffect(() => {
    checkOverflow();
    const handleResize = () => checkOverflow();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [normalizedScanDetails]);

  const wellnessValue = normalizedScanDetails[0].metrics[0].value;
  const series = useMemo(() => {
    const val = Number(normalizedScanDetails[0].metrics[0].value) || 0;
    return [Math.max(0, Math.min(10, val)) * 10];
  }, [normalizedScanDetails[0].metrics[0].value]);

  const options = {
    chart: { type: "radialBar", offsetY: 0, sparkline: { enabled: true } },
    plotOptions: {
      radialBar: {
        startAngle: -150,
        endAngle: 150,
        hollow: { size: "85%", background: "transparent" },
        track: { background: "#f0f0f0", strokeWidth: "100%", margin: 0 },
        dataLabels: {
          name: { offsetY: 30, show: true, color: "#574f8a", fontSize: "16px" },
          value: {
            offsetY: -10,
            fontSize: "29px",
            color: "#574f8a",
            fontWeight: 600,
            show: true,
            formatter: () => `${wellnessValue}/10`,
          },
        },
      },
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "horizontal",
        shadeIntensity: 0.4,
        inverseColors: false,
        opacityFrom: 1,
        opacityTo: 1,
        stops: [0, 25, 50, 75, 100],
        colorStops: [
          { offset: 0, color: "#00d4ff", opacity: 1 },
          { offset: 25, color: "#7c3aed", opacity: 1 },
          { offset: 50, color: "#ec4899", opacity: 1 },
          { offset: 75, color: "#f97316", opacity: 1 },
          { offset: 100, color: "#ef4444", opacity: 1 },
        ],
      },
    },
    stroke: { lineCap: "round" },
    labels: [getWellnessLabel(wellnessValue, screenLabels)],
  };

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    const scrollAmount = 150;
    scrollRef.current.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  const dynamicFilterItems = useMemo(() => {
    if (!Array.isArray(normalizedScanDetails)) return [];
    const items = normalizedScanDetails
      .filter((cat) => cat.category.toLowerCase() !== "wellness")
      .map((cat, index) => ({
        id: index + 1,
        key: cat.category.toLowerCase().replace(/\s/g, "_"),
        name: cat.category,
        ...(cat.category.toLowerCase() === "resources"
          ? { link: "/articles" }
          : {}),
      }));

    if (!items.some((item) => item.key === "resources")) {
      items.push({
        id: items.length + 1,
        key: "resources",
        name: "Resources",
        link: "/articles",
      });
    }

    items.sort((a, b) => {
      const indexA = filterOrder.indexOf(a.key);
      const indexB = filterOrder.indexOf(b.key);
      return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
    });

    return items;
  }, [normalizedScanDetails]);

  const renderFilters = () => (
    <div
      className="d-flex align-items-start gap-1 overflow-auto hide-scrollbar"
      ref={scrollRef}
    >
      {dynamicFilterItems.map((item) => {
        const { icon, color } = getCategoryStyle(item.key);
        const normalizedKey = item.key.toLowerCase();
        // if (["blood", "stress", "risks"].includes(normalizedKey)) return null; // temp hide
        const displayName = screenLabels?.[normalizedKey]?.label_value;
        return (
          <div className="filter-item text-center" key={item.id}>
            <button
              className={`filter-btn ${color}`}
              onClick={() =>
                navigate(item.link || "/view-more-details", {
                  state: { selectedFilter: item.key },
                })
              }
            >
              <img src={icon} alt={displayName} className="filter-icon" />
            </button>
            <span className="mb-0 pb-0" title={displayName}>
              {displayName}
            </span>
          </div>
        );
      })}
    </div>
  );

  let chartVal = normalizedScanDetails[0].metrics[0].value;

  if (loading) {
    return <Loader />;
  }
  return (
    <div className="content-part mt-2">
      {scanDetails && Object.keys(scanDetails || {}).length > 0 ? (
        <>
          <div
            className={`d-flex align-items-center justify-content-center ${chartVal ? "gap-5" : ""}`}
          >
            <div className="dashboard-logo">
              <img
                src={logoUrl || images.appLogo}
                alt="app-logo"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = images.appLogo;
                }}
              />
            </div>

            <div className="ml-2">
              {chartVal && (
                <>
                  <div className="mb-2 d-flex justify-content-center">
                    <Chart
                      key={chartVal}
                      options={options}
                      series={series}
                      type="radialBar"
                      height={160}
                      width={160}
                      className="chart"
                    />
                    <div
                      className="indicator-dot"
                      style={{ top: "15px", left: "135px" }}
                    ></div>
                  </div>
                  <div className="d-flex justify-content-center align-items-center flex-column">
                    <p
                      className="wellness-title mb-0 d-flex align-items-center gap-1 fs-15px"
                      onClick={() => {
                        openDrawer(
                          {
                            key: "wellness",
                            value: normalizedScanDetails[0].metrics[0].value,
                            unit: "/10",
                            status: normalizedScanDetails?.wellness?.status,
                            comment: normalizedScanDetails?.wellness?.comment,
                            avg: normalizedScanDetails?.wellness?.avg ?? null,
                            icon: statusIconMap[
                              normalizedScanDetails?.wellness?.status
                            ],
                          },
                          false,
                          "all",
                        );
                      }}
                    >
                      {screenLabels?.wellness_score?.label_value ||
                        "Wellness Score"}{" "}
                      <Info
                        className="text-primary"
                        size={18}
                        style={{ cursor: "pointer" }}
                      />
                    </p>
                    <p className="wellness-desc mb-0">
                      {normalizedScanDetails?.wellness?.comment}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          {chartVal && (
            <div className="my-3">
              <h6>
                {screenLabels?.wellness_insight?.label_value ||
                  "Wellness Insights"}
              </h6>
              <div className="wellness-insights-card shadow-sm">
                <p className="mb-0 small">
                  {getWellnessMessage(
                    normalizedScanDetails[0].metrics[0].value,
                    screenLabels,
                  )}
                </p>
              </div>
            </div>
          )}

          <div className="mb-2 d-flex justify-content-between align-items-center">
            <h6 className="mb-0">
              {screenLabels?.health_biomarkers?.label_value ||
                "Health Biomarkers"}
            </h6>
            {isOverflowing && (
              <div className="d-flex align-items-center gap-3">
                <div
                  className="previous-icon"
                  onClick={() => scroll("left")}
                  style={{ cursor: "pointer" }}
                >
                  <ChevronLeft />
                </div>
                <div
                  className="next-icon"
                  onClick={() => scroll("right")}
                  style={{ cursor: "pointer" }}
                >
                  <ChevronRight />
                </div>
              </div>
            )}
          </div>

          <div className="card">
            <div className="card-body">{renderFilters()}</div>
          </div>

          <section>
            <div className="d-flex justify-content-between mb-1">
              <h6>
                {screenLabels?.latest_indicators?.label_value ||
                  "Latest Indicators"}
              </h6>
              <Link
                to="/view-more-details"
                className="cursor-pointer text-decoration-none linkColor"
              >
                {screenLabels?.view_all?.label_value || "View All"}
              </Link>
            </div>

            <div>
              {healthCards.map((card, idx) => {
                const { icon, color } = getCategoryStyle(card.key);
                const isHeartHealth = card.category === "Heart Age";

                return (
                  <div
                    key={idx}
                    className={`card ${
                      is4Life
                        ? `fourlife-shadow-${card.key}`
                        : `shadow-${card.key}`
                    } d-flex flex-row justify-content-between px-2 py-2 mb-3`}
                    onClick={() =>
                      openDrawer(
                        {
                          ...card.rawMetric,
                          icon:
                            statusIconMap[card.rawMetric.status] ||
                            statusIconMap["normal"],
                        },
                        true,
                        card.category.toLowerCase().replace(/\s/g, ""),
                      )
                    }
                  >
                    <div className={`icon-wrapper ${color}`}>
                      <img
                        src={icon}
                        alt={card.category}
                        style={{ width: "24px", height: "24px" }}
                      />
                    </div>
                    <div className="flex-grow-1 px-2">
                      <h6 className="mb-0 fw-semibold welless-card-title">
                        {card.title}
                      </h6>
                      <div className="d-flex justify-content-between bp-details">
                        <div>
                          <p className="mb-0 font-bold fs-14px">
                            {isHeartHealth
                              ? screenLabels?.cronological_age?.label_value ||
                                "Chronological Age"
                              : screenLabels?.reading?.label_value || "Reading"}
                          </p>
                          <p className="mb-0 fs-14px fw-semibold">
                            {isHeartHealth
                              ? card?.rawMetric?.avg || "-"
                              : card.categoryScore}
                          </p>
                        </div>
                        <div>
                          <p className="mb-0 fs-14px text-end">
                            {isHeartHealth
                              ? screenLabels?.biological_age?.label_value ||
                                "Biological Age"
                              : wellnessLabel || "-"}
                          </p>
                          <p className="mb-0 fs-14px fw-semibold">
                            {isHeartHealth
                              ? card.categoryScore
                              : card?.rawMetric?.avg || "-"}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="info-icon me-1">
                      <Info size={18} />
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <HealthDetailDrawer
            show={showCardDetail}
            onClose={closeDrawer}
            activeTab={activeTab}
            showIconAndImage={showIconAndImage}
            metricData={selectedMetricData}
          />

          <BiometricDrawer />
        </>
      ) : (
        <>
          <div className="d-flex flex-column align-items-center justify-content-center text-center">
            <div className="mb-3">
              <img
                src={logoUrl || images.appLogo}
                alt="app-logo"
                style={{
                  width: "120px",
                  height: "120px",
                  objectFit: "contain",
                }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = images.appLogo;
                }}
              />
            </div>

            <h4 className="mb-2 fw-bold">
              {screenLabels?.no_health_data?.label_value ||
                "No Health Data Available"}
            </h4>
            <p className="text-muted" style={{ maxWidth: "400px" }}>
              {screenLabels?.start_scan_inst?.label_value ||
                "Start your health journey by scanning now! A quick scan helps you track vital biomarkers, understand your wellness score, and get personalized insights to improve your lifestyle."}
            </p>
            <div className="mt-3 small text-muted">
              <p>
                ✔
                {screenLabels?.scan_det_1?.label_value ||
                  "Quick and non-invasive"}
              </p>
              <p>
                ✔{" "}
                {screenLabels?.scan_det_2?.label_value ||
                  "Get detailed wellness insights"}
              </p>
              <p>
                ✔{" "}
                {screenLabels?.scan_det_3?.label_value ||
                  "Track your progress over time"}
              </p>
            </div>
            <button
              className="btn btn-primary mt-3 px-4"
              onClick={() => navigate("/start-scan")}
            >
              {screenLabels?.start_scan?.label_value || "Start Scan"}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
